<script setup lang="ts">
import { ROUTE_NAMES } from '~/constants/routes';
import { Locale } from '~i18n/i18nConfig';

// Cookiebot implementation reference
// https://support.cookiebot.com/hc/en-us/articles/360003798814-Changing-or-withdrawing-consent

// COMPOSABLES
const { tt } = useTypedI18n();
const { navMenuItems } = useNavMenuItems();
const config = useRuntimeConfig();
const gtmEnabled = !config.public.googleTagManager.disabled;
const { locale } = useI18n();

// DATA
const navItems = computed(() => navMenuItems.value.filter(item => !item?.children));

// HANDLERS
const top = (): void => scrollToTop();
</script>

<template>
    <footer class="bg-primary-950 text-inverted flex w-full justify-center">
        <BaseWidthContainer class="flex flex-col gap-8 py-8 md:gap-9 md:py-12 xl:py-16">
            <div class="flex flex-col gap-8">
                <div class="grid grid-cols-2 gap-x-5 gap-y-8 md:grid-cols-3 md:gap-4 xl:gap-8">
                    <div class="col-span-2 grid grid-cols-2 gap-x-5 gap-y-4 md:col-span-1 md:flex md:flex-col md:gap-4">
                        <div class="col-span-2">
                            <SvgoFleequidLogo class="h-6" :font-controlled="false" />
                        </div>
                        <FleequidFooterContacts />
                    </div>
                    <nav class="flex flex-col gap-3">
                        <h4 class="text-base font-semibold md:text-lg">{{ tt('footer.explore') }}</h4>
                        <NuxtLink
                            v-for="item in navItems"
                            :key="item.label"
                            class="text-xs hover:underline md:text-sm"
                            :to="item?.to"
                            :target="item.target"
                            @click="item?.onSelect"
                        >
                            {{ item.label }}
                        </NuxtLink>
                    </nav>
                    <nav class="flex flex-col gap-3">
                        <h4 class="text-base font-semibold md:text-lg">{{ tt('footer.legal') }}</h4>

                        <NuxtLinkLocale :to="ROUTE_NAMES.TERMS_AND_CONDITIONS" class="text-xs hover:underline md:text-sm">
                            {{ tt('termsConditions.link') }}
                        </NuxtLinkLocale>

                        <NuxtLinkLocale :to="ROUTE_NAMES.PRIVACY_POLICY" class="text-xs hover:underline md:text-sm">
                            {{ tt('privacy.link') }}
                        </NuxtLinkLocale>

                        <NuxtLinkLocale :to="ROUTE_NAMES.COOKIE_POLICY" class="text-xs hover:underline md:text-sm">
                            {{ tt('footer.cookiePolicy.base') }}
                        </NuxtLinkLocale>

                        <NuxtLinkLocale :to="ROUTE_NAMES.ACCESSIBILITY_STATEMENT" class="text-xs hover:underline md:text-sm">
                            {{ tt('footer.accessibilityStatement') }}
                        </NuxtLinkLocale>

                        <NuxtLinkLocale v-if="locale === Locale.DE" :to="ROUTE_NAMES.IMPRESSUM" class="text-xs hover:underline md:text-sm">
                            Impressum
                        </NuxtLinkLocale>

                        <template v-if="gtmEnabled">
                            <a class="text-xs hover:underline md:text-sm" href="javascript:Cookiebot.renew()">
                                {{ tt('footer.cookiePolicy.changeConsent') }}
                            </a>
                            <a class="text-xs hover:underline md:text-sm" href="javascript:Cookiebot.withdraw()">
                                {{ tt('footer.cookiePolicy.revokeConsent') }}
                            </a>
                        </template>
                    </nav>
                </div>
            </div>
            <USeparator class="opacity-20" />
            <div class="flex items-start justify-between sm:items-center">
                <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                    <p class="text-xs text-neutral-400"><FleequidCopyright /></p>
                </div>
                <UButton variant="link" color="neutral" size="xs" class="text-inverted hover:text-inverted active:text-inverted" @click="top">{{
                    tt('footer.backTop')
                }}</UButton>
            </div>

            <USeparator class="opacity-20" />

            <div class="flex flex-col gap-3 xl:flex-row xl:justify-between">
                <p class="text-xs text-neutral-400">{{ tt('gateway.stripeTitle') }} {{ tt('gateway.stripeDescription') }}</p>
                <div class="flex flex-wrap justify-start gap-3">
                    <UIcon name="pmt:pay-pal-logo" class="h-7 w-10" />
                    <UIcon name="pmt:google-pay" class="h-7 w-10" />
                    <UIcon name="pmt:apple-pay" class="h-7 w-10" />
                    <UIcon name="pmt:card-visa" class="h-7 w-10" />
                    <UIcon name="pmt:card-mastercard" class="h-7 w-10" :font-controlled="false" filled />
                    <UIcon name="pmt:card-amex" class="h-7 w-10" :font-controlled="false" filled />
                    <SvgoPoweredByStripe class="h-7 max-h-7 min-h-7" :font-controlled="false" filled />
                </div>
            </div>
        </BaseWidthContainer>
    </footer>
</template>
